import React from 'react';
import { useNavigate } from 'react-router-dom';

// Section display order — mirrors the backend SECTION_ORDER
const SECTION_ORDER = {
  fda: [
    'scope_facility', 'management_commitment',
    'gmp_prerequisite', 'ssop_general', 'allergen_control', 'supplier_control',
    'food_defense', 'recall_plan', 'pest_control', 'employee_training',
    'equipment_list', 'water_quality', 'waste_management', 'environmental_monitoring',
    'fda_product_descriptions', 'fda_flow_diagrams', 'fda_hazard_analysis',
    'fda_preventive_controls', 'fda_process_controls', 'fda_allergen_controls_pc',
    'fda_sanitation_controls', 'fda_supply_chain_controls', 'fda_allergen_matrix',
    'fda_pcqi_designation', 'fda_monitoring_records', 'fda_corrective_actions',
    'fda_verification', 'fda_reanalysis', 'fda_modifications_record',
  ],
  usda: [
    'scope_facility', 'management_commitment',
    'gmp_prerequisite', 'ssop_general', 'allergen_control', 'supplier_control',
    'food_defense', 'recall_plan', 'pest_control', 'employee_training',
    'equipment_list', 'water_quality', 'waste_management', 'environmental_monitoring',
    'usda_product_descriptions', 'usda_flow_diagrams', 'usda_hazard_analysis',
    'usda_haccp_plan_table', 'usda_prerequisite_programs', 'usda_allergen_matrix',
    'usda_haccp_coordinator', 'usda_monitoring_records', 'usda_corrective_actions',
    'usda_verification', 'usda_record_keeping', 'usda_modifications_record',
  ],
};

function fmt(d) {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt) ? d : dt.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

// ─── PlanSnapshotView ─────────────────────────────────────────────────────────
// Props:
//   snapshot    — snapshot object from the backend
//   version     — { versionNumber, approvedAt, signatureName, signatureRole }
//   showPrint   — show Print button
//   showBack    — show Back button
//   backTo      — path for Back button
//   backLabel   — label for Back button (default: "← Back")
export default function PlanSnapshotView({ snapshot, version, showPrint, showBack, backTo, backLabel }) {
  const navigate = useNavigate();
  if (!snapshot) return null;

  const { planType, name, companyName, sharedSections = {}, planSections = {}, products = [] } = snapshot;
  const order = SECTION_ORDER[planType] || [];

  const allSections = order
    .map(key => {
      const sec = sharedSections[key] || planSections[key];
      if (!sec) return null;
      const hasContent = !!(sec.content?.trim());
      return { key, title: sec.title || key, content: sec.content, contentType: sec.contentType, hasContent };
    })
    .filter(Boolean);

  const filledSections = allSections.filter(s => s.hasContent);

  const planLabel = planType === 'fda'
    ? 'FDA Food Safety Plan (21 CFR Part 117)'
    : 'USDA HACCP Plan (9 CFR 417)';

  return (
    <div style={{ fontFamily: 'Georgia, serif', background: '#fff', minHeight: '100vh' }}>
      {/* Print / action bar — hidden when printing */}
      <div className="no-print" style={{ background: '#f8f9fa', borderBottom: '1px solid #dee2e6', padding: '10px 32px', display: 'flex', alignItems: 'center', gap: 12, position: 'sticky', top: 0, zIndex: 100 }}>
        {showBack && backTo && (
          <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(backTo)}>
            {backLabel || '← Back'}
          </button>
        )}
        <span style={{ flex: 1, fontSize: 13, color: '#555' }}>
          {companyName && <><strong>{companyName}</strong> — </>}{name}
          {version?.versionNumber && <span className="badge bg-success ms-2">V{version.versionNumber}</span>}
        </span>
        {showPrint && (
          <button className="btn btn-primary btn-sm" onClick={() => window.print()}>
            🖨 Print / Save as PDF
          </button>
        )}
      </div>

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '40px 48px' }} className="plan-snapshot-body">

        {/* Cover page */}
        <div style={{ textAlign: 'center', marginBottom: 48, paddingBottom: 40, borderBottom: '3px double #333' }}>
          {companyName && (
            <div style={{ fontSize: 15, color: '#555', marginBottom: 8, letterSpacing: 1, textTransform: 'uppercase' }}>
              {companyName}
            </div>
          )}
          <h1 style={{ fontSize: 26, fontWeight: 700, marginBottom: 6 }}>{name || planLabel}</h1>
          <div style={{ fontSize: 14, color: '#666', marginBottom: 20 }}>{planLabel}</div>

          {version && (
            <table style={{ margin: '0 auto', fontSize: 13, borderCollapse: 'collapse' }}>
              <tbody>
                {version.versionNumber && (
                  <tr>
                    <td style={{ paddingRight: 24, color: '#555', textAlign: 'right' }}>Version:</td>
                    <td style={{ fontWeight: 600 }}>V{version.versionNumber}</td>
                  </tr>
                )}
                {version.approvedAt && (
                  <tr>
                    <td style={{ paddingRight: 24, color: '#555', textAlign: 'right' }}>Date Approved:</td>
                    <td style={{ fontWeight: 600 }}>{fmt(version.approvedAt)}</td>
                  </tr>
                )}
                {version.signatureName && (
                  <tr>
                    <td style={{ paddingRight: 24, color: '#555', textAlign: 'right' }}>Signed by:</td>
                    <td style={{ fontWeight: 600 }}>
                      {version.signatureName}{version.signatureRole ? `, ${version.signatureRole}` : ''}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          <div style={{ marginTop: 24, fontSize: 12, color: '#888' }}>
            {filledSections.length} of {allSections.length} sections completed
          </div>
        </div>

        {/* Products summary (if any) */}
        {products.length > 0 && (
          <div style={{ marginBottom: 40, pageBreakAfter: 'avoid' }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, borderBottom: '2px solid #333', paddingBottom: 6, marginBottom: 14 }}>
              Products Covered
            </h2>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f0f0f0' }}>
                  <th style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'left' }}>Product</th>
                  <th style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'left' }}>Intended Use</th>
                  <th style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'left' }}>Distribution</th>
                  <th style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'left' }}>Allergens</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ border: '1px solid #ccc', padding: '6px 10px', fontWeight: 600 }}>{p.name}</td>
                    <td style={{ border: '1px solid #ccc', padding: '6px 10px' }}>{p.intendedUse || '—'}</td>
                    <td style={{ border: '1px solid #ccc', padding: '6px 10px' }}>{p.distributionMethod || '—'}</td>
                    <td style={{ border: '1px solid #ccc', padding: '6px 10px' }}>{p.allergens || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Sections */}
        {allSections.map((sec, idx) => (
          <div key={sec.key} style={{ marginBottom: 40, pageBreakInside: 'avoid' }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, borderBottom: '1px solid #aaa', paddingBottom: 5, marginBottom: 14, color: '#1a1a1a' }}>
              <span style={{ color: '#888', fontSize: 12, marginRight: 8, fontWeight: 400 }}>{idx + 1}.</span>
              {sec.title}
            </h2>
            {sec.hasContent ? (
              <div
                className="ql-editor plan-section-body"
                style={{ padding: 0 }}
                dangerouslySetInnerHTML={{ __html: sec.content }}
              />
            ) : (
              <div style={{ color: '#aaa', fontStyle: 'italic', fontSize: 12 }}>
                [Section not completed]
              </div>
            )}
          </div>
        ))}

        {/* Signature block */}
        {version?.signatureName && (
          <div style={{ marginTop: 60, paddingTop: 24, borderTop: '2px solid #333' }}>
            <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 20 }}>Signatures & Approval</h2>
            <table style={{ fontSize: 12, borderCollapse: 'collapse', width: '100%' }}>
              <tbody>
                <tr>
                  <td style={{ width: '50%', padding: '8px 0', verticalAlign: 'bottom' }}>
                    <div style={{ borderBottom: '1px solid #333', paddingBottom: 2, marginBottom: 4, minHeight: 32 }}>
                      {version.signatureName}
                    </div>
                    <div style={{ color: '#555' }}>Name: {version.signatureName}</div>
                    {version.signatureRole && <div style={{ color: '#555' }}>Title: {version.signatureRole}</div>}
                  </td>
                  <td style={{ width: '30%', padding: '8px 24px', verticalAlign: 'bottom' }}>
                    <div style={{ borderBottom: '1px solid #333', paddingBottom: 2, marginBottom: 4, minHeight: 32 }}>
                      {fmt(version.approvedAt)}
                    </div>
                    <div style={{ color: '#555' }}>Date Signed</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <div style={{ marginTop: 40, fontSize: 10, color: '#aaa', textAlign: 'center' }}>
          Generated by quickLOG Food Safety Module · {companyName}
          {version?.approvedAt ? ` · Approved ${fmt(version.approvedAt)}` : ''}
        </div>
      </div>

      <style>{`
        /* ── Document body normalization ── */
        .plan-section-body,
        .plan-section-body * {
          font-family: Arial, 'Helvetica Neue', Helvetica, sans-serif !important;
          font-size: 11pt !important;
          line-height: 1.65 !important;
          color: #1a1a1a !important;
        }

        /* Force all body text to left-align regardless of editor inline styles */
        .plan-section-body p,
        .plan-section-body li,
        .plan-section-body td,
        .plan-section-body th,
        .plan-section-body blockquote {
          text-align: left !important;
        }

        /* Headings inside content — preserve hierarchy but normalise font */
        .plan-section-body h1 { font-size: 14pt !important; font-weight: 700 !important; margin: 14px 0 6px !important; }
        .plan-section-body h2 { font-size: 13pt !important; font-weight: 700 !important; margin: 12px 0 5px !important; border-bottom: none !important; padding-bottom: 0 !important; }
        .plan-section-body h3 { font-size: 11pt !important; font-weight: 700 !important; margin: 10px 0 4px !important; }

        /* Paragraphs */
        .plan-section-body p { margin: 0 0 6px !important; }

        /* Lists */
        .plan-section-body ul,
        .plan-section-body ol { padding-left: 22px !important; margin: 4px 0 8px !important; }
        .plan-section-body li { margin-bottom: 3px !important; }

        /* Tables */
        .plan-section-body table { width: 100% !important; border-collapse: collapse !important; margin: 8px 0 12px !important; }
        .plan-section-body th,
        .plan-section-body td { border: 1px solid #bbb !important; padding: 5px 8px !important; font-size: 10pt !important; vertical-align: top !important; }
        .plan-section-body th { background: #f0f0f0 !important; font-weight: 700 !important; }

        /* Blockquotes */
        .plan-section-body blockquote {
          border-left: 3px solid #aaa !important;
          padding-left: 12px !important;
          margin: 6px 0 !important;
          color: #444 !important;
        }

        /* Strong / em — keep visible but normalise size */
        .plan-section-body strong { font-weight: 700 !important; }
        .plan-section-body em { font-style: italic !important; }
        .plan-section-body u { text-decoration: underline !important; }

        /* ── Print ── */
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; }
          .plan-snapshot-body { padding: 20px 28px !important; }
          h2 { page-break-after: avoid; }
          .plan-section-body { font-size: 10pt !important; }
          .plan-section-body * { font-size: 10pt !important; }
          .plan-section-body h1 { font-size: 12pt !important; }
          .plan-section-body h2 { font-size: 11pt !important; }
          .plan-section-body th,
          .plan-section-body td { font-size: 9pt !important; }
        }
      `}</style>
    </div>
  );
}
